/**
 * Axios API client for the Flock backend.
 *
 * Pattern: localStorage tokens + Authorization: Bearer (proven adminpanel
 * approach — CSRF is bypassed server-side when a Bearer header is present).
 *
 * 401 handling: mutex-protected refresh via POST /auth/refresh
 * { refreshToken, organizationId }. The response envelope is nested:
 * { success, data: { token, refreshToken } }. On refresh failure we attempt
 * silent re-auth through Firebase (getIdToken → POST /auth/google), mirroring
 * flock-expo/src/services/api.ts; only when that also fails do we redirect
 * to /login.
 */
import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { auth } from '@/config/firebase';
import {
  clearAuthTokens,
  getCurrentOrgId,
  getRefreshToken,
  getToken,
  setRefreshToken,
  setToken,
} from '@/lib/storage';
import type { RefreshResponse } from '@/types/api.types';

export const API_BASE_URL: string =
  import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// ── Refresh mutex ─────────────────────────────────────────────────────────
let isRefreshing = false;
let isRedirecting = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];
const CACHE_BUSTED_GET_ENDPOINTS = ['/auth/me'];

function subscribeTokenRefresh(callback: (token: string | null) => void): void {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(token: string | null): void {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function redirectToLogin(): void {
  if (isRedirecting) return;
  isRedirecting = true;
  console.log('[API] Session expired, redirecting to login');
  clearAuthTokens();
  window.location.href = '/login';
}

// ── Subscription-lock refresh hook (PR 6) ──────────────────────────────────
// When an org locks mid-session, its REST calls start 403ing with
// SUBSCRIPTION_SUSPENDED. We ask AuthContext to refresh the session ONCE (max
// once per 60s) so per-org `subscription_locked` propagates and the inline
// lock UI renders. We never sign the user out on this — locking is per-org.
type SessionRefreshHandler = () => void;
let onSubscriptionLock: SessionRefreshHandler | null = null;
let lastLockRefreshAt = 0;
const LOCK_REFRESH_MIN_INTERVAL_MS = 60_000;

export function registerSubscriptionLockHandler(handler: SessionRefreshHandler | null): void {
  onSubscriptionLock = handler;
}

function handleSubscriptionLock403(): void {
  const now = Date.now();
  if (now - lastLockRefreshAt < LOCK_REFRESH_MIN_INTERVAL_MS) return;
  lastLockRefreshAt = now;
  onSubscriptionLock?.();
}

/**
 * Refresh the access token. Safe to call concurrently (single in-flight
 * refresh; concurrent callers await the same result).
 * Returns the new access token, or null on failure.
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (isRefreshing) {
    return new Promise((resolve) => subscribeTokenRefresh(resolve));
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  isRefreshing = true;
  try {
    // Raw axios (not `api`) so interceptors don't recurse.
    const response = await axios.post<RefreshResponse>(`${API_BASE_URL}/auth/refresh`, {
      refreshToken,
      // Preserve multi-org context: backend re-syncs Firebase claims to this org
      organizationId: getCurrentOrgId() || undefined,
    });

    const newToken = response.data?.data?.token;
    const newRefreshToken = response.data?.data?.refreshToken;
    if (!newToken) throw new Error('Refresh response missing token');

    setToken(newToken);
    if (newRefreshToken) setRefreshToken(newRefreshToken);

    onTokenRefreshed(newToken);
    return newToken;
  } catch (error) {
    console.warn('[API] Token refresh failed:', (error as Error).message);

    // Silent re-auth fallback: if we still have a live Firebase session,
    // mint a fresh backend session from the Firebase ID token.
    const recovered = await attemptSilentReauth();
    onTokenRefreshed(recovered);
    return recovered;
  } finally {
    isRefreshing = false;
  }
}

async function attemptSilentReauth(): Promise<string | null> {
  try {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return null;

    console.log('[API] Attempting silent re-auth via Firebase session');
    const idToken = await firebaseUser.getIdToken(true);
    // Legacy backend route name: verifies any Firebase ID token provider
    // (email/password, Google, Apple) and mints a Flock backend session.
    const response = await axios.post(`${API_BASE_URL}/auth/google`, {
      idToken,
      platform: 'web',
    });

    const data = response.data?.data;
    if (!data?.token) return null;

    setToken(data.token);
    if (data.refreshToken) setRefreshToken(data.refreshToken);
    console.log('[API] Silent re-auth successful');
    return data.token as string;
  } catch (error) {
    console.warn('[API] Silent re-auth failed:', (error as Error).message);
    return null;
  }
}

// ── Axios instance ────────────────────────────────────────────────────────
const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  const token = getToken();
  const orgId = getCurrentOrgId();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Per-request X-Organization-Id wins — cross-org features (events calendar,
  // prayer combined views) pin requests to a specific org the user belongs to.
  // The backend verifies membership of whatever org the header names.
  if (orgId && !config.headers['X-Organization-Id']) {
    config.headers['X-Organization-Id'] = orgId;
  }
  // NOTE: no X-Request-Correlation-Id here — the backend's CORS allow-list
  // (Access-Control-Allow-Headers) does not include it, so browsers reject
  // the preflight. Mobile can send it because native requests skip CORS.

  // Prevent stale cached session payloads after org/user switches without
  // disabling browser/CDN caching for every data GET in the app.
  const method = (config.method || 'get').toLowerCase();
  const shouldCacheBust = method === 'get'
    && CACHE_BUSTED_GET_ENDPOINTS.some((path) => config.url?.includes(path));
  if (shouldCacheBust) {
    config.params = { ...config.params, _t: Date.now() };
  }

  return config;
});

const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/google',
  '/auth/apple',
  '/auth/signup',
  '/auth/refresh',
  '/auth/logout',
];

client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<any>) => {
    const originalRequest = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      const isAuthEndpoint = AUTH_ENDPOINTS.some((p) => originalRequest.url?.includes(p));
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      const newToken = await refreshAccessToken();
      if (newToken) {
        originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newToken}` };
        return client(originalRequest);
      }

      redirectToLogin();
      return Promise.reject(error);
    }

    // Suspended account: backend returns 403 with error code
    if (error.response?.status === 403 && error.response.data?.error === 'ACCOUNT_SUSPENDED') {
      console.warn('[API] Account suspended');
      redirectToLogin();
    }

    // Org locked mid-session: refresh the session so inline lock UI renders.
    // Do NOT sign out — only the locked org is affected, other orgs work.
    if (error.response?.status === 403 && error.response.data?.error === 'SUBSCRIPTION_SUSPENDED') {
      handleSubscriptionLock403();
    }

    return Promise.reject(error);
  }
);

// ── Typed helpers (same call shape as flock-expo's api service) ──────────
export const api = {
  get: async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    (await client.get<T>(url, config)).data,
  post: async <T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    (await client.post<T>(url, data, config)).data,
  put: async <T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    (await client.put<T>(url, data, config)).data,
  patch: async <T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    (await client.patch<T>(url, data, config)).data,
  delete: async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    (await client.delete<T>(url, config)).data,
};

export default client;
